import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardEdit,
  CheckSquare,
  Trophy,
  Award,
  Layers,
  Users,
  Info,
  ListChecks,
  CalendarClock,
  Skull,
  History,
} from "lucide-react";

const NavItem = ({ to, icon: Icon, label, onNavigate, end }) => (
  <li>
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        `sidebar-link${isActive ? " is-active" : ""}`
      }
    >
      <Icon className="sidebar-link__icon" size={20} strokeWidth={2} />
      <span>{label}</span>
    </NavLink>
  </li>
);

/**
 * Persistent left-hand navigation for the logged-in app shell.
 *
 * Desktop: always visible, sticky down the left-hand side.
 * Mobile (<1024px): off-canvas drawer, toggled via the hamburger
 * button in the Header and closed via `onNavigate`/backdrop click.
 */
const Sidebar = ({ isOpen, onClose, isAdmin, hasProfile }) => {
  const closeOnMobile = () => onClose?.();

  return (
    <>
      <div
        className={`sidebar-backdrop${isOpen ? " is-open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`sidebar${isOpen ? " is-open" : ""}`}
        aria-label="Main navigation"
      >
        <nav>
          {!isAdmin && hasProfile && (
            <div className="sidebar-group">
              <span className="sidebar-group__label">Main</span>
              <ul>
                <NavItem
                  to="/dashboard"
                  icon={LayoutDashboard}
                  label="Dashboard"
                  onNavigate={closeOnMobile}
                />
                <NavItem
                  to="/predictions"
                  icon={ClipboardEdit}
                  label="Predictions"
                  onNavigate={closeOnMobile}
                />
                <NavItem
                  to="/results"
                  icon={CheckSquare}
                  label="Results"
                  onNavigate={closeOnMobile}
                />
              </ul>
            </div>
          )}

          {!isAdmin && hasProfile && (
            <div className="sidebar-group">
              <span className="sidebar-group__label">Stats</span>
              <ul>
                <NavItem
                  to="/leaderboard"
                  icon={Trophy}
                  label="Leaderboard"
                  onNavigate={closeOnMobile}
                />
                <NavItem
                  to="/roll-of-honour"
                  icon={Award}
                  label="Roll of Honour"
                  onNavigate={closeOnMobile}
                />
                <NavItem
                  to="/parallel-universe"
                  icon={Layers}
                  label="Parallel Universe"
                  onNavigate={closeOnMobile}
                />
                <NavItem
                  to="/team-breakdown"
                  icon={Users}
                  label="Team Breakdown"
                  onNavigate={closeOnMobile}
                />
              </ul>
            </div>
          )}

          {isAdmin && (
            <div className="sidebar-group">
              <span className="sidebar-group__label">Admin</span>
              <ul>
                <NavItem
                  to="/admin/results"
                  icon={ListChecks}
                  label="Results"
                  onNavigate={closeOnMobile}
                />
                <NavItem
                  to="/admin/seasons"
                  icon={CalendarClock}
                  label="Seasons"
                  onNavigate={closeOnMobile}
                />
              </ul>
            </div>
          )}

          {!isAdmin && (
            <div className="sidebar-group">
              <ul>
                <NavItem
                  to="/how-it-works"
                  icon={Info}
                  label="How It Works"
                  onNavigate={closeOnMobile}
                />
              </ul>
            </div>
          )}

          {/* Last Man Standing is a standalone game, kept visually
              separate from the main predictions league nav above. */}
          <div className="sidebar-group sidebar-group--separated">
            <span className="sidebar-group__label">Last Man Standing</span>
            <ul>
              {isAdmin ? (
                <NavItem
                  to="/admin/last-man-standing"
                  icon={Skull}
                  label="Manage Rounds"
                  onNavigate={closeOnMobile}
                />
              ) : (
                hasProfile && (
                  <NavItem
                    to="/last-man-standing"
                    icon={Skull}
                    label="Play"
                    onNavigate={closeOnMobile}
                    end
                  />
                )
              )}
              {/* History and How It Works are player-facing pages —
                  Admins manage rounds from the admin page above instead,
                  which already includes its own round history. */}
              {!isAdmin && (
                <NavItem
                  to="/last-man-standing/history"
                  icon={History}
                  label="History"
                  onNavigate={closeOnMobile}
                />
              )}
              {!isAdmin && (
                <NavItem
                  to="/last-man-standing/how-it-works"
                  icon={Info}
                  label="How It Works"
                  onNavigate={closeOnMobile}
                />
              )}
            </ul>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
